/**
 * The black circle in the bottom-right corner, shown only to Kono super users.
 *
 * Clicking it asks for the password again before the price-provenance tools
 * appear. That re-entry is not a formality: it advances the Firebase token's
 * `auth_time`, which is the only thing the API accepts as proof, so a session
 * someone left open cannot reprice the catalog without it. Clicking the circle
 * again puts the tools away immediately.
 *
 * Staff who signed in through Google or Microsoft have no password to re-enter,
 * so they re-authenticate through the same provider popup instead — the token
 * ends up equally fresh either way.
 */
import { useCallback, useEffect, useState } from "react";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from "firebase/auth";
import { firebaseAuth } from "../../firebase";
import { useSuperUserMode } from "../../buyerStateDefs";
import { fetchSuperUserSession } from "./superUserApi";

/** How the signed-in account can prove itself again. */
const reauthMethod = (): "password" | "google" | "microsoft" | null => {
  const providers = (firebaseAuth.currentUser?.providerData || []).map(
    (p) => p.providerId,
  );
  // Password first: an account with both can always be verified without a popup,
  // which browsers may block.
  if (providers.includes("password")) return "password";
  if (providers.includes("google.com")) return "google";
  if (providers.includes("microsoft.com")) return "microsoft";
  return null;
};

export const SuperUserToggle = () => {
  const active = useSuperUserMode((state) => state.active);
  const expiresAt = useSuperUserMode((state) => state.expiresAt);
  const activate = useSuperUserMode((state) => state.activate);
  const deactivate = useSuperUserMode((state) => state.deactivate);

  const [prompting, setPrompting] = useState(false);
  const [password, setPassword] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");

  // Drop the tools the moment the window closes, so the UI never offers an edit
  // the API is already refusing.
  useEffect(() => {
    if (!active || !expiresAt) return;
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) {
      deactivate();
      return;
    }
    const timer = setTimeout(deactivate, remaining);
    return () => clearTimeout(timer);
  }, [active, expiresAt, deactivate]);

  const closePrompt = useCallback(() => {
    setPrompting(false);
    setPassword("");
    setError("");
  }, []);

  const verify = useCallback(async () => {
    const user = firebaseAuth.currentUser;
    if (!user) return;
    setVerifying(true);
    setError("");
    try {
      const method = reauthMethod();
      if (method === "password") {
        if (!user.email) throw new Error("This account has no email address");
        await reauthenticateWithCredential(
          user,
          EmailAuthProvider.credential(user.email, password),
        );
      } else if (method === "google") {
        await reauthenticateWithPopup(user, new GoogleAuthProvider());
      } else if (method === "microsoft") {
        await reauthenticateWithPopup(user, new OAuthProvider("microsoft.com"));
      } else {
        throw new Error("This account cannot be re-authenticated");
      }

      // Force a refresh so the token carries the just-advanced `auth_time`; the
      // API reads its own freshness from the token, not from this component.
      await user.getIdToken(true);
      const session = await fetchSuperUserSession();
      activate(new Date(session.expiresAt).getTime());
      closePrompt();
    } catch (e: any) {
      console.error("Super-user re-authentication failed", e);
      setError(
        e?.code === "auth/wrong-password" || e?.code === "auth/invalid-credential"
          ? "That password is not right."
          : e?.code === "auth/too-many-requests"
            ? "Too many attempts. Wait a moment and try again."
            : e?.code === "auth/popup-closed-by-user"
              ? "The sign-in window closed before it finished."
              : e?.message || "Could not verify. Try again.",
      );
    } finally {
      setVerifying(false);
    }
  }, [password, activate, closePrompt]);

  const method = reauthMethod();

  return (
    <>
      <button
        type="button"
        onClick={() => (active ? deactivate() : setPrompting(true))}
        // Sits below the cookie banner and the modals (z-[100] and above) so it
        // can't cover a consent choice. The banner's wrapper lets clicks through,
        // so this stays usable while it is up; only at phone width does the card
        // itself reach this corner.
        className={`fixed bottom-5 right-5 z-50 h-10 w-10 rounded-full shadow-lg transition
          ${
            active
              ? "bg-[#00062e] ring-4 ring-amber-400"
              : "bg-black hover:bg-[#00062e]"
          }`}
        aria-pressed={active}
        aria-label={
          active
            ? "Turn off Kono super user tools"
            : "Turn on Kono super user tools"
        }
        title={
          active
            ? "Kono super user tools are on. Click to hide them."
            : "Kono super user tools"
        }
      />

      {prompting && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-[#00062e]">
              Confirm it&apos;s you
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {method === "password"
                ? "Enter your password to turn on the Kono super user tools."
                : "Sign in again with your Kono account to turn on the super user tools."}
            </p>

            {method === "password" && (
              <form
                className="mt-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  verify();
                }}
              >
                <input
                  type="password"
                  autoFocus
                  autoComplete="current-password"
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button type="submit" className="hidden" />
              </form>
            )}

            {error && <div className="mt-3 text-sm text-red-600">{error}</div>}

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={verify}
                disabled={
                  verifying || (method === "password" && password.length === 0)
                }
                className="flex-1 rounded-full bg-[#00062e] py-2 font-bold text-white disabled:opacity-50"
              >
                {verifying ? "Checking…" : "Confirm"}
              </button>
              <button
                type="button"
                onClick={closePrompt}
                className="flex-1 rounded-full border-2 border-[#00062e] py-2 font-bold text-[#00062e]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
